import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { defineMessages, injectIntl } from "react-intl";

import Button from "../Button/Button";
import Input, { getHeight as getInputHeight } from "../FormField/Input";
import SIZES from "../../../constants/sizes";
import KEY_CODES from "../../../constants/keyCodes";

import clearSearchIcon from "./clear-search-thin.svg";
import searchIcon from "./magnifying-glass.svg";

const searchIconSize = 24;
const iconsMargin = 8;
const clearIconSize = 12;
const clearIconInteractiveSize = 38;

const messages = defineMessages({
  searchIcon: {
    defaultMessage: "search icon"
  },
  clearIcon: {
    defaultMessage: "clear icon"
  }
});

const InputOverrided = styled(Input)`
  padding-left: ${iconsMargin * 2 + searchIconSize}px;
  padding-right: ${iconsMargin + clearIconInteractiveSize}px;
  border-top-right-radius: ${props => (props.showButton ? 0 : "inherit")};
  border-bottom-right-radius: ${props => (props.showButton ? 0 : "inherit")};
  outline-offset: -5px;
`;

const SearchContainer = styled.div`
  display: flex;
`;

const InputField = styled.div`
  position: relative;
  flex-grow: 1;
`;

const ClearIcon = styled.img`
  position: absolute;
  box-sizing: content-box;
  right: 0;
  top: ${props => (props.inputHeight - clearIconInteractiveSize) / 2}px;
  width: ${clearIconSize}px;
  height: ${clearIconSize}px;
  padding: ${(clearIconInteractiveSize - clearIconSize) / 2}px;
  cursor: pointer;
`;

const SearchIcon = styled.img`
  position: absolute;
  left: ${iconsMargin}px;
  top: ${props => (props.inputHeight - searchIconSize) / 2}px;
  width: ${searchIconSize}px;
  height: ${searchIconSize}px;
`;

const ButtonOverrided = styled(Button)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin: 0;
  outline-offset: -5px;
`;

const SearchField = ({
  inputRef,
  value,
  onRemove,
  onSearch,
  showButton,
  onKeyDown,
  buttonLabel,
  size,
  intl,
  ...rest
}) => {
  const defaultOnKeyDown = event => {
    if (event.keyCode === KEY_CODES.ENTER) {
      onSearch(value);
    }
    if (event.keyCode === KEY_CODES.ESC) {
      onRemove();
    }
  };

  return (
    <SearchContainer>
      <InputField>
        {value.length > 0 && (
          <ClearIcon
            src={clearSearchIcon}
            onClick={onRemove}
            inputHeight={getInputHeight(size)}
            showButton={showButton}
            alt={intl.formatMessage(messages.clearIcon)}
          />
        )}
        <SearchIcon
          src={searchIcon}
          inputHeight={getInputHeight(size)}
          alt={intl.formatMessage(messages.searchIcon)}
        />
        <InputOverrided
          {...rest}
          value={value}
          size={size}
          ref={inputRef}
          onKeyDown={onKeyDown || defaultOnKeyDown}
        />
      </InputField>
      {showButton && (
        <ButtonOverrided look="regular" onClick={onSearch} size={size}>
          {buttonLabel}
        </ButtonOverrided>
      )}
    </SearchContainer>
  );
};

SearchField.defaultProps = {
  size: SIZES.MEDIUM,
  onSearch: () => null,
  showButton: false,
  buttonLabel: "Search"
};

SearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  showButton: PropTypes.bool,
  buttonLabel: PropTypes.string,
  size: PropTypes.oneOf([SIZES.MEDIUM, SIZES.LARGE, SIZES.BIG]),
  inputRef: PropTypes.func
};

export default injectIntl(SearchField);
